import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import './PublicInvoiceView.css'

/**
 * The page a buyer lands on from the invoice SMS link.
 *
 * Deliberately outside the authenticated app: buyers are not DirectAuto users.
 * It renders only what the server chose to expose — no purchase price, no
 * margin, no seller, no internal ids.
 */

interface PublicInvoice {
  invoiceNumber: string
  invoiceDate: string
  dueDate: string
  buyerName: string
  buyerCompany: string
  vehicle: string
  subtotal: number
  gst: number
  total: number
  received: number
  outstanding: number
  status: string
}

const money = (n: number) =>
  `$${Number(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const auDate = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'long', year: 'numeric' }) : '—'

export default function PublicInvoiceView() {
  const { token } = useParams()
  const [invoice, setInvoice] = useState<PublicInvoice | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch(`/api/invoice/${token}`)
        const body = await res.json().catch(() => ({}))
        if (cancelled) return
        if (!res.ok || !body?.success) {
          setError(body?.error || 'This invoice link is not valid.')
        } else {
          setInvoice(body.invoice)
        }
      } catch {
        if (!cancelled) setError('Could not load this invoice. Please try again shortly.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [token])

  if (loading) {
    return (
      <div className="pinv-page">
        <div className="pinv-card"><p className="pinv-muted">Loading your invoice…</p></div>
      </div>
    )
  }

  if (error || !invoice) {
    return (
      <div className="pinv-page">
        <div className="pinv-card">
          <div className="pinv-brand">Direct Auto</div>
          <h1>Invoice unavailable</h1>
          <p className="pinv-muted">{error}</p>
          <p className="pinv-muted">
            If you were sent this link recently, please contact Direct Auto and ask for a new one.
          </p>
        </div>
      </div>
    )
  }

  const paid = invoice.status === 'paid'
  const overdue = !paid && new Date(invoice.dueDate) < new Date()

  return (
    <div className="pinv-page">
      <div className="pinv-card">
        <div className="pinv-head">
          <div className="pinv-brand">Direct Auto</div>
          <span className={`pinv-status${paid ? ' paid' : overdue ? ' overdue' : ''}`}>
            {paid ? 'Paid' : overdue ? 'Overdue' : 'Due'}
          </span>
        </div>

        <h1>Tax invoice {invoice.invoiceNumber}</h1>
        <p className="pinv-muted">
          {invoice.buyerCompany || invoice.buyerName || 'Buyer'} · issued {auDate(invoice.invoiceDate)}
        </p>

        {invoice.vehicle && (
          <div className="pinv-vehicle">
            <span>Vehicle</span>
            <strong>{invoice.vehicle}</strong>
          </div>
        )}

        <table className="pinv-table">
          <tbody>
            <tr><td>Subtotal</td><td>{money(invoice.subtotal)}</td></tr>
            <tr><td>GST</td><td>{money(invoice.gst)}</td></tr>
            <tr className="pinv-total"><td>Total</td><td>{money(invoice.total)}</td></tr>
            {invoice.received > 0 && (
              <tr><td>Received</td><td>−{money(invoice.received)}</td></tr>
            )}
            {!paid && (
              <tr className="pinv-outstanding"><td>Amount due</td><td>{money(invoice.outstanding)}</td></tr>
            )}
          </tbody>
        </table>

        {!paid && (
          <p className="pinv-due">
            Payment due <strong>{auDate(invoice.dueDate)}</strong>.
          </p>
        )}

        <p className="pinv-foot">
          Please use the invoice number as your payment reference. Questions about this invoice should
          go to Direct Auto directly — this page is a read-only copy and is not a payment page.
        </p>
      </div>
    </div>
  )
}
